import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import AllInclusive from '@mui/icons-material/AllInclusive';
import Computer from '@mui/icons-material/Computer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlaystation, faXbox } from '@fortawesome/free-brands-svg-icons';

import {
  getGameIcon,
  getModeTrad,
  getWeaponFit,
  getWeaponIcon,
  getWeaponTrad,
  KILLS_INFINITE,
} from '../../helpers';
import { Video } from '../../types';
import styles from './style';

const PLATFORM_LABELS: { [platform: string]: string } = {
  PC: 'PC',
  Playstation: 'PlayStation',
  Xbox: 'Xbox',
};

interface VideoInfoLineProps {
  video: Video;
  // Mobile: the game is already in the column on the right. Same order as the app: the mode,
  // then the weapon and the kills side by side.
  hideGame?: boolean;
  // Rows of the ranking: smaller cards, the platform, the weapon and the kills only.
  compact?: boolean;
}

// Same icons as the app: Material "computer" for PC, FontAwesome brands for the consoles.
const getPlatformIcon = (platform: string) => {
  if (platform === 'Playstation') {
    return <FontAwesomeIcon icon={faPlaystation} />;
  }
  if (platform === 'Xbox') {
    return <FontAwesomeIcon icon={faXbox} />;
  }
  return <Computer />;
};

// One line under the description, each value in its card.
// Platform, game and weapon first, as icons only (the name in the tooltip): a long name like
// "Call of Duty Black Ops 6" doesn't fit. Then the mode and the kills as text.
const VideoInfoLine: React.FC<VideoInfoLineProps> = React.memo((props) => {
  const { video, hideGame, compact } = props;
  const { t } = useTranslation();

  const weapon = video.weapon ? getWeaponTrad(video.weapon, t) : '';
  const showGame = !!video.gameName && !hideGame && !compact;
  // "Others" says nothing about the clip: the card is hidden.
  const showMode = !!video.mode && video.mode !== 'Others' && !compact;
  const iconCardSx = compact ? styles.iconCardSmall : styles.iconCard;
  const cardSx = compact ? styles.cardSmall : styles.card;
  // Only the tilt for now: the sizes of the table are still being tuned.
  const weaponIconSx = {
    ...(compact ? styles.weaponIconSmall : styles.weaponIcon),
    transform: `rotate(${getWeaponFit(video.weapon).rotate}deg)`,
  };

  // Above 6 the kills are "infinite": the same Material icon as the app (all-inclusive), centered
  // in an icon card. A text glyph sits on the font metrics and looks small and off center.
  let killsCard = null;
  if (video.kills >= KILLS_INFINITE) {
    killsCard = (
      <Box component="span" title={t('admin.feed.killsInfinite')} sx={compact ? styles.infiniteCardSmall : styles.infiniteCard}>
        <AllInclusive />
      </Box>
    );
  } else if (video.kills > 0) {
    killsCard = <Box component="span" sx={cardSx}>{t('admin.feed.kills', { count: video.kills })}</Box>;
  }

  return (
    <Box sx={styles.line}>
      {PLATFORM_LABELS[video.platform] && (
        <Box component="span" title={PLATFORM_LABELS[video.platform]} sx={iconCardSx}>
          {getPlatformIcon(video.platform)}
        </Box>
      )}
      {showGame && (
        <Box component="span" title={video.gameName} sx={styles.iconCard}>
          <Box component="img" src={getGameIcon(video.gameName)} alt={video.gameName} sx={styles.gameIcon} />
        </Box>
      )}
      {/* Mobile (game in the action bar): the order of the app, the mode then the weapon and the kills. */}
      {hideGame && showMode && <Box component="span" sx={styles.mode}>{getModeTrad(video.mode, t)}</Box>}
      {video.weapon && (
        <Box component="span" title={weapon} sx={iconCardSx}>
          <Box component="img" src={getWeaponIcon(video.weapon)} alt={weapon} sx={weaponIconSx} />
        </Box>
      )}
      {hideGame && killsCard}
      {!hideGame && showMode && <Box component="span" sx={styles.mode}>{getModeTrad(video.mode, t)}</Box>}
      {!hideGame && killsCard}
    </Box>
  );
});

export { PLATFORM_LABELS, getPlatformIcon };

export default VideoInfoLine;
